'use client';

import { useMemo } from 'react';
import useSWR from 'swr';
import dayjs from 'dayjs';
import { myPlannerApi } from './my-planner.api';
import type { PlannerCounselSlotResponse } from './planner-counsel-slot.api';

const VALID_STATUSES = new Set(['APPOINTED', 'AVAILABLE']);

function hasWorkSchedule(slots: PlannerCounselSlotResponse['slots']): boolean {
  return slots?.some((s) => VALID_STATUSES.has(s.status)) ?? false;
}

async function fetchMyWorkScheduleDates(startDate: string, endDate: string): Promise<string[]> {
  const responses = await myPlannerApi.getMySlotsByDateRange(startDate, endDate);
  const workDates = new Set<string>();
  for (const r of responses) {
    if (r.date && hasWorkSchedule(r.slots)) {
      workDates.add(r.date);
    }
  }
  return Array.from(workDates).sort();
}

const RANGE_DAYS = 90;

/**
 * PLANNER 전용: 현재 플래너의 근무일 목록 조회
 * /api/v1/planners/me/visit-counsels/slots 사용
 *
 * `isLoaded` 는 SWR 응답이 한 번이라도 도착했는지 여부 (`data !== undefined`).
 * 빈 배열로 도착한 경우(=응답은 왔지만 근무일이 없는 경우)와 아직 fetch 중인
 * 경우를 구분해 캘린더 휴무 마킹을 정확히 결정하기 위해 사용한다.
 */
export function useMyPlannerWorkScheduleDates(
  selectedDate: string,
  enabled = true
): { workDates: string[]; isLoaded: boolean; isLoading: boolean; error: Error | null } {
  const startDate = useMemo(
    () => dayjs(selectedDate).subtract(RANGE_DAYS, 'day').format('YYYY-MM-DD'),
    [selectedDate]
  );
  const endDate = useMemo(
    () => dayjs(selectedDate).add(RANGE_DAYS, 'day').format('YYYY-MM-DD'),
    [selectedDate]
  );

  const { data, error, isLoading } = useSWR<string[]>(
    enabled && selectedDate ? ['my-planner-work-schedule-dates', startDate, endDate] : null,
    () => fetchMyWorkScheduleDates(startDate, endDate),
    { keepPreviousData: true }
  );

  return {
    workDates: data ?? [],
    isLoaded: data !== undefined,
    isLoading,
    error: error ? (error instanceof Error ? error : new Error(String(error))) : null,
  };
}
